import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ImageCarousel from '../components/ImageCarousel';
import PriceDisplay from '../components/PriceDisplay';
import { ArrowRight, ShoppingCart, Leaf, Truck, Shield, CreditCard } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

/* ── component ───────────────────────────────────────────────────── */
const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [plans, setPlans] = useState([]);
  const [categories, setCategories] = useState([]);
  const [hero, setHero] = useState({ heading: '', subheading: '', button_text: '', button_link: '', background_image: '' });
  const [brandStory, setBrandStory] = useState({
    heading: 'From Earth.<br />For Ritual.',
    body_paragraph_1: 'Deep in the mist-clad valleys of Darjeeling and Assam, where the first light touches dew-kissed leaves, our journey begins. Each leaf is handpicked at the perfect moment — when nature is most generous.',
    body_paragraph_2: 'We believe tea is more than a beverage. It is a pause, a ritual, a return to something pure. IND-PETRICHOR PVT. LTD. partners with small-scale farmers who tend their gardens with the same care they would a family heirloom.',
    image_url: '',
    button_text: 'Read More',
    button_link: '/page/about',
  });

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL;
    Promise.all([
      axios.get(`${base}/api/products`),
      axios.get(`${base}/api/blogs`),
      axios.get(`${base}/api/subscriptions/plans`),
      axios.get(`${base}/api/cms/categories`),
      axios.get(`${base}/api/cms/hero`),
      axios.get(`${base}/api/cms/brand-story`),
    ]).then(([p, b, s, c, h, bs]) => {
      const allProducts = p.data;
      setFeaturedProducts(allProducts.slice(0, 4));
      const sorted = [...allProducts].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      setNewArrivals(sorted.slice(0, 4));
      setBlogs(b.data.slice(0, 3));
      setPlans(s.data);
      setCategories(c.data);
      if (h.data) setHero(h.data);
      if (bs.data) setBrandStory((prev) => ({ ...prev, ...bs.data }));
    }).catch(() => {});
  }, []);

  const addToCart = async (e, productId) => {
    e.stopPropagation();
    if (!user) { navigate('/login'); return; }
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/cart`, { product_id: productId, quantity: 1 }, { withCredentials: true });
      toast.success('Added to cart');
    } catch { toast.error('Failed to add to cart'); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />

      {/* ═══════════════════════════════════════════════════════════
          1  HERO  —  full-width, above the fold
          ═══════════════════════════════════════════════════════════ */}
      <section
        className="relative w-full pt-16 lg:pt-0"
        data-testid="hero-section"
      >
        {/* IMAGE area */}
        <div className="w-full h-[75vh] overflow-hidden lg:h-auto lg:overflow-visible">
          {hero.background_image && (
            <img
              src={hero.background_image}
              alt="Tea plantation"
              className="w-full h-full object-cover object-center opacity-0 animate-fade-in-slow lg:h-auto lg:object-contain"
              data-testid="hero-image"
            />
          )}
        </div>

        {/* TEXT overlay */}
        <div className="relative w-full flex flex-col items-center justify-end px-6 py-6 text-center lg:absolute lg:inset-0 lg:justify-end lg:pb-[20%] lg:pointer-events-none">
          <div className="w-full max-w-lg mx-auto px-6 py-8 pointer-events-auto flex flex-col items-center justify-center">
            {hero.heading && (
              <h1
                className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white font-bold leading-[1.15] mb-6 lg:mb-8 opacity-0 animate-fade-in stagger-1 lg:[text-shadow:0_2px_8px_rgba(0,0,0,0.35)]"
                data-testid="hero-heading"
              >
                {hero.heading}
              </h1>
            )}
            {hero.subheading && (
              <p
                className="text-white/70 text-base sm:text-lg max-w-lg mx-auto mb-8 lg:mb-12 leading-relaxed opacity-0 animate-fade-in stagger-2 lg:[text-shadow:0_2px_8px_rgba(0,0,0,0.35)]"
                data-testid="hero-subtext"
              >
                {hero.subheading}
              </p>
            )}
            <div className="opacity-0 animate-fade-in stagger-3">
              <Button
                onClick={() => navigate(hero.button_link || '/products')}
                size="lg"
                className="rounded-full bg-accent hover:bg-accent/90 text-white font-semibold text-sm px-10 h-12"
                data-testid="hero-shop-now-button"
              >
                {hero.button_text || 'Shop Now'}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          2  CATEGORIES  —  4-grid
          ═══════════════════════════════════════════════════════════ */}
      <section className="py-24 bg-[#FAFAFA]" data-testid="categories-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.3em] font-semibold text-secondary mb-3">Our Collection</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#1A237E]" data-testid="categories-heading">
              Explore by Type
            </h2>
          </div>

          <div className="flex flex-wrap justify-center gap-5">
            {categories.map((cat) => {
              const allImages = (cat.images && cat.images.length > 0) ? cat.images : (cat.image_url ? [cat.image_url] : []);
              return (
                <div
                  key={cat.name}
                  onClick={() => navigate(`/products?category=${cat.name}`)}
                  className="cursor-pointer group relative rounded-2xl overflow-hidden aspect-[3/4] w-[calc(50%-10px)] lg:w-[calc(25%-15px)]"
                  data-testid={`category-${cat.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <ImageCarousel images={allImages} alt={cat.name} className="absolute inset-0 w-full h-full" />
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-colors duration-500 z-[1]" />
                  <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6 z-[2]">
                    <h3 className="font-heading text-white text-xl sm:text-2xl font-semibold mb-1">{cat.name}</h3>
                    <p className="text-white/60 text-xs sm:text-sm">{cat.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          3  BRAND STORY  —  split layout
          ═══════════════════════════════════════════════════════════ */}
      <section className="py-24 bg-[#F5F5F5]" data-testid="brand-story-section">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            {/* LEFT: image */}
            <div className="rounded-2xl overflow-hidden aspect-[4/3]">
              {brandStory.image_url && (
                <img
                  src={brandStory.image_url}
                  alt="Misty tea plantation at sunrise"
                  className="w-full h-full object-cover"
                  data-testid="brand-story-image"
                />
              )}
            </div>
            {/* RIGHT: text */}
            <div className="lg:pl-8 max-w-lg">
              <p className="text-xs uppercase tracking-[0.3em] font-semibold text-secondary mb-4">Our Story</p>
              <h2
                className="font-heading text-3xl sm:text-4xl font-semibold text-[#1A237E] mb-6 leading-tight"
                data-testid="brand-story-heading"
                dangerouslySetInnerHTML={{ __html: brandStory.heading || 'From Earth.<br />For Ritual.' }}
              />
              <p className="text-[#1A237E]/70 leading-relaxed mb-4">
                {brandStory.body_paragraph_1}
              </p>
              <p className="text-[#1A237E]/70 leading-relaxed mb-8">
                {brandStory.body_paragraph_2}
              </p>
              <Button
                variant="outline"
                className="rounded-full border-[#1A237E]/30 text-[#1A237E] hover:bg-[#1A237E] hover:text-white transition-all text-sm px-8"
                onClick={() => navigate(brandStory.button_link || '/page/about')}
                data-testid="brand-story-read-more"
              >
                {brandStory.button_text || 'Read More'} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          4  FEATURED PRODUCTS
          ═══════════════════════════════════════════════════════════ */}
      {featuredProducts.length > 0 && (
        <section className="py-24 bg-[#FAFAFA]" data-testid="featured-products-section">
          <div className="container mx-auto px-4">
            <div className="flex items-end justify-between mb-14">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] font-semibold text-secondary mb-3">Curated for You</p>
                <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#1A237E]">Best Sellers</h2>
              </div>
              <Button
                variant="outline"
                onClick={() => navigate('/products')}
                className="rounded-full hidden md:inline-flex text-sm"
                data-testid="view-all-products-button"
              >
                View All <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {featuredProducts.map((product) => (
                <div
                  key={product.product_id}
                  className="group cursor-pointer"
                  onClick={() => navigate(`/products/${product.product_id}`)}
                  data-testid={`featured-product-${product.product_id}`}
                >
                  <div className="rounded-xl overflow-hidden bg-white border border-[#E0E0E0] mb-4 transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-lg">
                    <div className="aspect-square overflow-hidden relative">
                      {product.images && product.images.length > 1 ? (
                        <ImageCarousel images={product.images} alt={product.name} className="w-full h-full" />
                      ) : (
                        <img src={product.image_url} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                      )}
                    </div>
                  </div>
                  <p className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-1">{product.category}</p>
                  <h3 className="font-heading font-semibold text-sm sm:text-base mb-1.5 leading-snug">{product.name}</h3>
                  <div className="flex items-center justify-between">
                    <PriceDisplay price={product.price} originalPrice={product.original_price} size="md" />
                    <Button
                      size="sm"
                      className="rounded-full h-8 px-3 text-xs"
                      onClick={(e) => addToCart(e, product.product_id)}
                      data-testid={`add-to-cart-featured-${product.product_id}`}
                    >
                      <ShoppingCart className="h-3 w-3 mr-1" /> Add
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-10 md:hidden">
              <Button variant="outline" onClick={() => navigate('/products')} className="rounded-full text-sm">
                View All <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════════════════════════
          4b  NEW ARRIVALS
          ═══════════════════════════════════════════════════════════ */}
      {newArrivals.length > 0 && (
        <section className="py-24 bg-[#F5F5F5]" data-testid="new-arrivals-section">
          <div className="container mx-auto px-4">
            <div className="flex items-end justify-between mb-14">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] font-semibold text-secondary mb-3">Fresh Picks</p>
                <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#1A237E]">New Arrivals</h2>
              </div>
              <Button variant="outline" onClick={() => navigate('/products')} className="rounded-full hidden md:inline-flex text-sm" data-testid="view-all-new-arrivals-button">
                View All <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {newArrivals.map((product) => (
                <div key={product.product_id} className="group cursor-pointer" onClick={() => navigate(`/products/${product.product_id}`)} data-testid={`new-arrival-${product.product_id}`}>
                  <div className="rounded-xl overflow-hidden bg-white border border-[#E0E0E0] mb-4 transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-lg">
                    <div className="aspect-square overflow-hidden relative">
                      {product.images && product.images.length > 1 ? (
                        <ImageCarousel images={product.images} alt={product.name} className="w-full h-full" />
                      ) : (
                        <img src={product.image_url} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                      )}
                    </div>
                  </div>
                  <p className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-1">{product.category}</p>
                  <h3 className="font-heading font-semibold text-sm sm:text-base mb-1.5 leading-snug">{product.name}</h3>
                  <div className="flex items-center justify-between">
                    <PriceDisplay price={product.price} originalPrice={product.original_price} size="md" />
                    <Button size="sm" className="rounded-full h-8 px-3 text-xs" onClick={(e) => addToCart(e, product.product_id)} data-testid={`add-to-cart-new-${product.product_id}`}>
                      <ShoppingCart className="h-3 w-3 mr-1" /> Add
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════════════════════════
          5  RITUAL SUBSCRIPTION
          ═══════════════════════════════════════════════════════════ */}
      <section className="py-24 bg-[#1A237E]" data-testid="subscription-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.3em] font-semibold text-accent mb-3">Monthly Ritual</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-4">
              A Curated Box of Wonder
            </h2>
            <p className="text-white/50 max-w-lg mx-auto text-sm leading-relaxed">
              Premium teas, tasting notes, and brewing guides — delivered to your doorstep. Cancel anytime.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            {plans.map((plan) => (
              <div
                key={plan.plan_id}
                className="p-8 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm hover:border-accent/40 transition-colors"
                data-testid={`plan-${plan.plan_id}`}
              >
                <h3 className="font-heading text-white text-xl font-semibold mb-1">{plan.name}</h3>
                <p className="text-white/40 text-sm mb-5">{plan.description}</p>
                <p className="text-3xl font-bold text-accent mb-1">
                  {'\u20B9'}{plan.price}
                  <span className="text-sm font-normal text-white/40">/{plan.interval}</span>
                </p>
                <ul className="space-y-2 my-6">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-white/60">
                      <span className="text-accent mt-0.5">-</span> {f}
                    </li>
                  ))}
                </ul>
                <Button
                  className="w-full rounded-full bg-accent hover:bg-accent/90 text-white font-semibold text-sm"
                  onClick={() => navigate('/subscription')}
                  data-testid={`subscribe-now-${plan.plan_id}`}
                >
                  Subscribe Now
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          6  BLOG SECTION
          ═══════════════════════════════════════════════════════════ */}
      {blogs.length > 0 && (
        <section className="py-24 bg-[#FAFAFA]" data-testid="blog-section">
          <div className="container mx-auto px-4">
            <div className="text-center mb-14">
              <p className="text-xs uppercase tracking-[0.3em] font-semibold text-secondary mb-3">Tea Journal</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#1A237E]">Stories & Guides</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {blogs.map((blog) => (
                <div
                  key={blog.blog_id}
                  className="group cursor-pointer"
                  onClick={() => navigate(`/blogs/${blog.slug}`)}
                  data-testid={`blog-card-${blog.blog_id}`}
                >
                  <div className="rounded-xl overflow-hidden mb-5 aspect-[4/3] bg-white border border-[#E0E0E0]">
                    {blog.image_url ? (
                      <img
                        src={blog.image_url}
                        alt={blog.title}
                        className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full bg-muted" />
                    )}
                  </div>
                  <p className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-2">{blog.author}</p>
                  <h3 className="font-heading font-semibold text-lg mb-2 group-hover:text-primary transition-colors leading-snug">{blog.title}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{blog.excerpt}</p>
                  <span className="text-sm text-primary font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Read More <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════════════════════════
          7  TRUST SECTION
          ═══════════════════════════════════════════════════════════ */}
      <section className="py-20 bg-[#F5F5F5]" data-testid="trust-section">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Leaf,       title: 'Natural Ingredients',  desc: '100% organic, single-origin tea leaves' },
              { icon: Truck,      title: 'Fast Delivery',        desc: 'Free shipping on orders above \u20B9500' },
              { icon: CreditCard, title: 'Secure Payments',      desc: 'Powered by Razorpay with 256-bit SSL' },
              { icon: Shield,     title: 'Reliable Shipping',    desc: 'Tracked delivery via Shiprocket' },
            ].map((item) => (
              <div key={item.title} className="text-center" data-testid={`trust-${item.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-4">
                  <item.icon className="h-5 w-5 text-primary" />
                </div>
                <h4 className="font-heading font-semibold text-sm mb-1">{item.title}</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
